// Standard asset classes used throughout the app - Canadian-biased terminology
export const standardAssetClasses = ['equity', 'fixed_income', 'alternative', 'cash', 'multi_asset', 'unclassified'];

// Mapping for consistent asset class normalization
const ASSET_CLASS_MAPPING: Record<string, string> = {
  'alternative': 'alternative',
  'equity': 'equity', 
  'fixed_income': 'fixed_income',
  'cash': 'cash',
  'multi_asset': 'multi_asset',
  'unclassified': 'unclassified'
};

// Labels for display in charts
export const ASSET_CLASS_LABELS: Record<string, string> = {
  'equity': 'Equity',
  'fixed_income': 'Fixed Income',
  'cash': 'Cash Equivalent',
  'alternative': 'Private Alternative',
  'multi_asset': 'Multi-Asset',
  'unclassified': 'Unclassified'
};

// Compact labels for chart legends
export const PIE_CHART_LABELS: Record<string, string> = {
  'equity': 'Equity',
  'fixed_income': 'Income',
  'cash': 'Cash',
  'alternative': 'Alternative',
  'multi_asset': 'Multi-Asset',
  'unclassified': 'Unclassified'
};

/**
 * Maps arbitrary fund asset classes to our standardized categories
 * 
 * @param assetClass Original asset class string from fund data
 * @param assetType Optional asset type for fallback classification
 * @returns Standardized asset class suitable for charts and UI
 */
export function determineStandardAssetClass(assetClass?: string, assetType?: string): string {
  // Default to 'unclassified' if we can't determine the asset class
  let standardClass = 'unclassified';
  
  // Special case: stock_fundamental asset type is always 'equity'
  if (assetType === 'stock_fundamental') {
    return 'equity';
  }
  
  // First priority: Try to map from assetClass if present
  if (assetClass) {
    const lowerClass = assetClass.toLowerCase();
    
    // Map common variations to standard asset classes (Canadian terminology)
    if (lowerClass.includes('equity') || lowerClass.includes('stock')) {
      standardClass = 'equity';
    } else if (lowerClass.includes('bond') || lowerClass.includes('fixed') || lowerClass.includes('income')) {
      standardClass = 'fixed_income';
    } else if (lowerClass.includes('cash') || lowerClass.includes('money market')) {
      standardClass = 'cash';
    } else if (lowerClass.includes('alternative') || lowerClass.includes('alt') || lowerClass.includes('crypto') || lowerClass.includes('bitcoin') || lowerClass.includes('btc')) {
      standardClass = 'alternative';
    } else if (lowerClass.includes('multi') || lowerClass.includes('mixed')) {
      standardClass = 'multi_asset';
    } else if (standardAssetClasses.includes(lowerClass)) {
      standardClass = lowerClass;
    }
  } 
  // Second priority: use asset_type for classification
  else if (assetType) {
    const lowerCaseAssetType = assetType.toLowerCase();
    if (standardAssetClasses.includes(lowerCaseAssetType)) {
      standardClass = lowerCaseAssetType;
    } else if (lowerCaseAssetType.includes('equity') || lowerCaseAssetType.includes('stock')) {
      // Normalize synonyms to our standard classes
      standardClass = 'equity';
    } else if (lowerCaseAssetType.includes('bond') || lowerCaseAssetType.includes('fixed') || lowerCaseAssetType.includes('income')) {
      standardClass = 'fixed_income';
    } else if (lowerCaseAssetType.includes('cash') || lowerCaseAssetType.includes('money market')) {
      standardClass = 'cash';
    } else if (lowerCaseAssetType.includes('multi') || lowerCaseAssetType.includes('mixed')) {
      standardClass = 'multi_asset';
    } else if (lowerCaseAssetType.includes('crypto') || lowerCaseAssetType.includes('bitcoin') || lowerCaseAssetType.includes('btc')) {
      // Special handling for crypto assets - classify as alternative
      standardClass = 'alternative';
    }
    // Note: fund_raw and fund types without asset_class remain 'unclassified'
  }
  
  return standardClass;
}
